import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { HEADERS, type ApiFailure, type PaginationMeta } from '@stockpilot/shared';
import { loggedOut, tokensRefreshed } from '@/features/auth/authSlice';
import type { RootState } from '@/app/store';

const API_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/** The envelope every successful API response arrives in. */
interface Envelope<T> {
  success: true;
  data: T;
  meta?: PaginationMeta | Record<string, unknown>;
}

export interface ListResult<T> {
  items: T[];
  meta: PaginationMeta;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  // Sends the httpOnly refresh cookie.
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;

    if (state.auth.accessToken) {
      headers.set('authorization', `Bearer ${state.auth.accessToken}`);
    }
    // The API validates this against the user's memberships; it is a hint, not
    // an authorization.
    if (state.tenant.organizationId) {
      headers.set(HEADERS.ORGANIZATION, state.tenant.organizationId);
    }
    if (state.tenant.branchId) {
      headers.set(HEADERS.BRANCH, state.tenant.branchId);
    }
    return headers;
  },
});

/**
 * Refreshes ONCE on a 401 and replays the original request.
 *
 * The in-flight promise is shared so a burst of parallel 401s produces a single
 * refresh — otherwise every concurrent request would rotate the token and the
 * API's reuse detection would (correctly) kill the session.
 */
let refreshPromise: Promise<boolean> | null = null;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  const isAuthRoute =
    typeof args === 'string'
      ? args.startsWith('/auth/')
      : String(args.url).startsWith('/auth/');

  if (result.error?.status === 401 && !isAuthRoute) {
    refreshPromise ??= (async () => {
      const state = api.getState() as RootState;
      const refreshResult = await rawBaseQuery(
        {
          url: '/auth/refresh',
          method: 'POST',
          body: { refreshToken: state.auth.refreshToken ?? undefined },
        },
        api,
        extraOptions,
      );

      const payload = refreshResult.data as Envelope<{
        accessToken: string;
        refreshToken: string;
      }> | undefined;

      if (payload?.data?.accessToken) {
        api.dispatch(tokensRefreshed(payload.data));
        return true;
      }

      api.dispatch(loggedOut());
      return false;
    })().finally(() => {
      refreshPromise = null;
    });

    const refreshed = await refreshPromise;
    if (refreshed) result = await rawBaseQuery(args, api, extraOptions);
  }

  return result;
};

export const TAGS = [
  'Auth',
  'Session',
  'Organization',
  'OrganizationContext',
  'Settings',
  'Feature',
  'Subscription',
  'Branch',
  'Warehouse',
  'Member',
  'Department',
  'Role',
  'Stock',
  'StockLedger',
  'Category',
  'Brand',
  'Unit',
  'UnitConversion',
  'Product',
  'Transfer',
  'StockCount',
  'Customer',
  'CustomerGroup',
  'CustomerStatement',
  'Sale',
  'Payment',
  'PaymentMethod',
  'SalesReturn',
  'Invoice',
  'PosSession',
  'HeldCart',
  'Supplier',
  'PurchaseOrder',
  'GoodsReceipt',
  'SupplierInvoice',
  'SupplierReturn',
  'Expense',
  'Account',
  'Property',
  'PropertySale',
  'AccountTransaction',
  'ExpenseCategory',
  'Audit',
  'Notification',
] as const;

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: TAGS,
  // Data still matters after 60s of inactivity in a back-office app.
  keepUnusedDataFor: 120,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});

/** Unwraps `{ success, data }` for a single resource. */
export const unwrap = <T>(response: Envelope<T>): T => response.data;

/** Unwraps a paginated list into `{ items, meta }`. */
export const unwrapList = <T>(response: Envelope<T[]>): ListResult<T> => ({
  items: response.data,
  meta: (response.meta as PaginationMeta) ?? {
    page: 1,
    limit: response.data.length,
    total: response.data.length,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  },
});

/** Pulls a human-usable message out of an RTK Query error. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (!error || typeof error !== 'object') return fallback;

  const data = (error as { data?: unknown }).data;
  if (data && typeof data === 'object' && 'message' in data) {
    const failure = data as ApiFailure;

    // A validation message on its own — "correct the highlighted fields" — is a
    // dead end whenever a field the API names has no input bound to it on this
    // form. Naming the fields costs a little repetition and removes the case
    // where the reader is told to fix something invisible.
    if (failure.errors?.length) {
      const named = failure.errors
        .map((entry) => `${humanise(entry.field)}: ${entry.message}`)
        .join(' · ');
      return `${failure.message} — ${named}`;
    }

    return String(failure.message);
  }
  if ('status' in (error as Record<string, unknown>)) {
    const status = (error as { status: unknown }).status;
    if (status === 'FETCH_ERROR') return 'Cannot reach the server. Check your connection.';
    if (status === 'PARSING_ERROR') return 'The server returned an unexpected response.';
  }
  return fallback;
}

/** `accountId` reads as "Account", which is what the form calls it. */
function humanise(field: string): string {
  const words = field
    .replace(/Id$/, '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Field-level errors, for attaching messages to inputs. */
export function apiFieldErrors(error: unknown): Record<string, string> {
  const data = (error as { data?: ApiFailure } | undefined)?.data;
  if (!data?.errors) return {};
  return Object.fromEntries(data.errors.map((entry) => [entry.field, entry.message]));
}

export function apiErrorCode(error: unknown): string | undefined {
  const data = (error as { data?: ApiFailure } | undefined)?.data;
  return data?.code;
}
